import { GalleryItem } from '@app/gallery/gallery.service';
import { GoalSearch } from '@app/goal/goal.service';
import { ProgramCategorySearch } from '@app/program-category/program-category.service';
import { LevelSearch } from '@app/level/level.service';
import { EnvironmentSearch } from '@app/exercise/exercise.service';
import { ExpectedResult } from '@app/nutrition-program/types';

export type ProgramStageKey = 'draft' | 'soon' | 'live';

export interface ProgramStageUpdate {
    id: number;
    stage: ProgramStageKey;
}

export interface ProgramBulkStageRequest {
    program_ids: number[];
    stage: ProgramStageKey;
}

export interface ProgramSearch {
    id: number;
    name: string;
    author: Author;
    length_in_weeks: number;
    level_names?: string;
    environment_names?: string;
    location_names?: string;
    is_published: boolean;
    stage?: ProgramStageKey;
    like_count: number;
    avg_rating: string;
    comment_count: number;
    activated_count: number;
    deactivated_count: number;
    is_display_only: boolean;
    is_tailor_made?: boolean;
    is_from_library?: boolean;
    is_coming_soon?: boolean;
    is_free?: boolean;
    order?: number;
    language?: { code: string; name: string } | null;
    tag_names?: string;
    tag_ids?: number[];
    tags?: Array<{ id: number; name: string; is_custom_tag?: boolean }>;
    image_url?: string;
    image?: GalleryItem | null;
    workout_count?: number | null;
    workouts_count?: number | null;
    trainings_count?: number | null;
    active_users?: ProgramActiveUser[];
}

export interface ProgramActiveUser {
    id: number;
    name: string;
    avatar?: string;
    email?: string;
}

export interface Author {
    id: number;
    name: string;
    role: string;
}

export interface Authors {
    value: string;
    name: string;
}

export interface ProgramItemCustomerResponse {
    id: number;
    name: string;
}

export interface WeekItemAlternative {
    id: number;
    name: string;
    training_copy_of?: number | null;
    is_program_fork?: boolean;
    training_type_names?: string | null;
}

export interface ProgramWeekItemResponse {
    id: number;
    program_week_id: number;
    training_id?: number | null;
    is_locked: boolean;
    is_rest_day: boolean;
    order: number;
    is_program_fork?: boolean;
    training_copy_of?: number | null;
    alternative_training_ids?: number[];
    alternatives?: WeekItemAlternative[];
    training?: Training | null;
}
export interface Training {
    id: number;
    name: string;
    description: string;
    number_of_calories: number;
    approximate_duration_in_minutes: number;
    parent_id: number;
    training_type_names?: string | null;
}
export interface ProgramWeekResponse {
    id: number;
    program_id: number;
    order: number;
    week_items: ProgramWeekItemResponse[];
    description: string;
}

export interface ExpectedResults {
    description: string;
}

export interface ProgramResponse {
    id: number;
    user_id: number;
    name: string;
    internal_name: string;
    description: string;
    gender: 'male' | 'female';
    language?: { code: string; name: string } | null;
    workout_days_per_week: number;
    is_published: boolean;
    publish_at?: string;
    is_free: boolean;
    is_display_only: boolean;
    is_tailor_made: boolean;
    is_coming_soon: boolean;
    follows_schedule: boolean;
    parent_program?: Program;
    stripe_product_id?: string;
    stripe_payment_link?: string;
    shopify_product_id?: string;
    order?: number;
    image: GalleryItem;
    rest_day_image: GalleryItem;
    video: GalleryItem;
    categories: ProgramCategorySearch[];
    levels: LevelSearch[];
    exercise_environments: EnvironmentSearch[];
    goals: GoalSearch[];
    expected_results: ExpectedResults[];
    program_weeks: ProgramWeekResponse[];
    muscle_groups: MuscleGroup[];
    instructors: ProgramInstructor[];
    documents: any;
    price: string;
    currency_code: string;
    questionnaire?: Questionnaire;
    readiness_questionnaire?: Questionnaire;
    include_in_survey?: boolean;
    only_with_one_time_payment?: boolean;
    package_id?: number;
    number_of_renewals?: number | null;
    continue_renewing?: boolean;
    notify_coach_on_purchase?: boolean;
    program_packages?: ProgramPackage[];
    tag_ids?: number[];
    tags?: Array<{ id: number; name: string; is_custom_tag?: boolean }>;
    stage?: ProgramStageKey;
    live_sync?: boolean;
    activated_count?: number;
}

export interface ProgramPackage {
    package_id: number;
    automatic_activation: boolean;
}

export interface MuscleGroup {
    id: number;
    name: string;
}

export interface ProgramInstructor {
    id: number;
    name: string;
    order: string | number;
}

export interface WeekItem {
    is_locked: boolean;
    training_id?: number;
    make_copy?: boolean;
    name: string;
    is_rest_day: boolean;
    order?: number;
    duration?: number | null;
    typeName?: string;
    is_from_library?: boolean;
    image_url?: string;
    is_program_fork?: boolean;
    training_copy_of?: number | null;
    alternative_training_ids?: number[];
    alternatives?: WeekItemAlternative[];
}

export interface ProgramWeek {
    id?: number;
    description: string;
    items: WeekItem[];
}

export interface Program {
    id?: number;
    name: string;
    internal_name: string;
    description: string;
    workout_days_per_week: number;
    category_ids: number[];
    level_ids: number[];
    goal_ids: number[];
    exercise_environment_ids: number[];
    is_published: boolean;
    publish_at?: string | Date;
    is_free: boolean;
    is_display_only: boolean;
    is_tailor_made: boolean;
    is_coming_soon: boolean;
    follows_schedule: boolean;
    parent_program?: Program;
    stripe_product_id?: string;
    stripe_payment_link?: string;
    shopify_product_id?: string;
    order?: number;
    gender?: 'male' | 'female';
    language?: string | null;
    video_id: number;
    image_id: number;
    image: GalleryItem;
    video: GalleryItem;
    rest_day_image: GalleryItem;
    expected_results: ExpectedResult[];
    weeks: ProgramWeek[];
    muscle_groups: MuscleGroup[];
    instructors: ProgramInstructor[];
    documents?: any;
    price?: string;
    currency_code?: string;
    questionnaire?: Questionnaire;
    readiness_questionnaire?: Questionnaire;
    include_in_survey: boolean;
    one_time_purchase?: boolean;
    only_with_one_time_payment?: boolean;
    package_id?: number;
    number_of_renewals?: number | null;
    continue_renewing?: boolean;
    notify_coach_on_purchase?: boolean;
    program_packages?: ProgramPackage[];
    tag_ids?: number[];
    min_age?: number | null;
    max_age?: number | null;
    stage?: ProgramStageKey;
    live_sync?: boolean;
    activated_count?: number;
}

interface Questionnaire {
    description: string;
    id: number;
    name: string;
}
